/**
 * STICKER TEMPLATES V4 - AVISOCAR PREMIUM
 * ========================================
 * 100+ templates across 10 categories with rich visual design.
 * Each template has: palette, layout, background, icons for PRO rendering.
 */

// Color palettes for premium designs
export const PALETTES = {
  // Warm palettes
  latam_sunset: { primary: "#DC2626", secondary: "#FBBF24", accent: "#FFFFFF", bg: "linear-gradient(135deg, #DC2626 0%, #FBBF24 100%)" },
  peru_pride: { primary: "#DC2626", secondary: "#10B981", accent: "#FFFFFF", bg: "linear-gradient(180deg, #DC2626 50%, #FFFFFF 50%)" },
  golden_hour: { primary: "#F59E0B", secondary: "#FDE68A", accent: "#1E293B", bg: "linear-gradient(135deg, #F59E0B 0%, #FDE68A 100%)" },
  coral_dream: { primary: "#F97316", secondary: "#FDBA74", accent: "#FFFFFF", bg: "linear-gradient(135deg, #F97316 0%, #FEE2E2 100%)" },
  
  // Cool palettes
  ocean_blue: { primary: "#0EA5E9", secondary: "#38BDF8", accent: "#FFFFFF", bg: "linear-gradient(135deg, #0EA5E9 0%, #BAE6FD 100%)" },
  neon_night: { primary: "#8B5CF6", secondary: "#C4B5FD", accent: "#FFFFFF", bg: "linear-gradient(135deg, #1E1B4B 0%, #4C1D95 100%)" },
  emerald_forest: { primary: "#10B981", secondary: "#6EE7B7", accent: "#FFFFFF", bg: "linear-gradient(135deg, #059669 0%, #D1FAE5 100%)" },
  arctic_frost: { primary: "#06B6D4", secondary: "#A5F3FC", accent: "#0F172A", bg: "linear-gradient(135deg, #ECFEFF 0%, #A5F3FC 100%)" },
  
  // Dark palettes
  midnight_pro: { primary: "#1E293B", secondary: "#475569", accent: "#F8FAFC", bg: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)" },
  dark_alert: { primary: "#DC2626", secondary: "#1E293B", accent: "#FFFFFF", bg: "linear-gradient(180deg, #1E293B 60%, #DC2626 100%)" },
  
  // Vibrant palettes
  rainbow_pop: { primary: "#EC4899", secondary: "#8B5CF6", accent: "#FFFFFF", bg: "linear-gradient(135deg, #EC4899 0%, #8B5CF6 50%, #06B6D4 100%)" },
  candy_pink: { primary: "#EC4899", secondary: "#FBCFE8", accent: "#FFFFFF", bg: "linear-gradient(135deg, #EC4899 0%, #FBCFE8 100%)" },
  lime_energy: { primary: "#84CC16", secondary: "#D9F99D", accent: "#1E293B", bg: "linear-gradient(135deg, #84CC16 0%, #ECFCCB 100%)" },
  
  // Special palettes
  warning_zone: { primary: "#EAB308", secondary: "#FEF3C7", accent: "#1E293B", bg: "linear-gradient(135deg, #EAB308 0%, #FEF3C7 100%)" },
  emergency_red: { primary: "#DC2626", secondary: "#FEE2E2", accent: "#FFFFFF", bg: "linear-gradient(135deg, #DC2626 0%, #991B1B 100%)" },
  trust_blue: { primary: "#2563EB", secondary: "#DBEAFE", accent: "#FFFFFF", bg: "linear-gradient(135deg, #2563EB 0%, #60A5FA 100%)" },
};

// Layout types for different sticker styles
export const LAYOUTS = {
  hero: "Large emoji/icon at top, bold title, QR in card below",
  badge: "Compact badge style with border frame and icons",
  comic: "Fun cartoon style with speech bubbles and characters",
  minimal: "Clean, simple design with just essentials",
  warning: "Alert-style with warning stripes and bold text",
  card: "Card-style with rounded corners and shadow",
  split: "Two-tone split design",
  banner: "Wide banner style with icons on sides",
};

// Category metadata
export const STICKER_CATEGORIES = [
  { id: "vehiculos", name: "Vehículos", icon: "🚗", description: "Para autos, motos y más", color: "#EF4444" },
  { id: "objetos", name: "Objetos / Maletas", icon: "🎒", description: "Protege tus pertenencias", color: "#F97316" },
  { id: "ninos", name: "Niños", icon: "🧒", description: "Seguridad para los pequeños", color: "#FBBF24" },
  { id: "mascotas", name: "Mascotas", icon: "🐶", description: "Para tu mejor amigo", color: "#84CC16" },
  { id: "viajes", name: "Viajes", icon: "🧳", description: "Equipaje protegido", color: "#06B6D4" },
  { id: "emergencias", name: "Emergencias", icon: "🚨", description: "Información vital", color: "#DC2626" },
  { id: "privacidad", name: "Privacidad", icon: "🔒", description: "Contacto privado", color: "#6366F1" },
  { id: "creativos", name: "Creativos", icon: "✨", description: "Divertidos y originales", color: "#EC4899" },
  { id: "minimalista", name: "Minimalista", icon: "🏷️", description: "Simple y elegante", color: "#64748B" },
  { id: "premium_latam", name: "Premium LATAM", icon: "⭐", description: "Especial para Perú", color: "#10B981" },
];

// MAIN TEMPLATES - 100+ premium designs
export const STICKER_TEMPLATES = [
  // ========================================
  // 🚗 VEHÍCULOS (10 templates)
  // ========================================
  {
    id: "v1",
    category: "vehiculos",
    title: "¿Mal Estacionado?",
    slogan: "¡Escanea para avisar al dueño!",
    subline: "Contacto privado con AvisoCar",
    emoji: "🚗",
    palette: "golden_hour",
    layout: "hero",
    background: { type: "gradient", pattern: "diagonal-lines" },
    icons: ["car", "phone", "alert", "light"],
    featured: true
  },
  {
    id: "v2",
    category: "vehiculos",
    title: "¿Problemas con tu Auto?",
    slogan: "Escanea y avisa al dueño",
    subline: "Contacto privado y seguro",
    emoji: "🚙",
    palette: "trust_blue",
    layout: "badge",
    background: { type: "gradient", pattern: "dots" },
    icons: ["car", "tow", "emergency", "key"]
  },
  {
    id: "v3",
    category: "vehiculos",
    title: "¿Estacionado en Problemas?",
    slogan: "Escanea para alertar al propietario",
    subline: "Comunicación privada y segura",
    emoji: "🚔",
    palette: "dark_alert",
    layout: "warning",
    background: { type: "solid", pattern: "warning-stripes" },
    icons: ["police", "tow", "alert", "grua"]
  },
  {
    id: "v4",
    category: "vehiculos",
    title: "¡Escanea y Avisa al Dueño!",
    slogan: "Fácil y privada",
    subline: "Emergencia • Llamada Privada",
    emoji: "📱",
    palette: "emergency_red",
    layout: "split",
    background: { type: "split", colors: ["#FFFFFF", "#DC2626"] },
    icons: ["emergency", "phone", "car", "light"]
  },
  {
    id: "v5",
    category: "vehiculos",
    title: "Si bloqueo, aquí estoy",
    slogan: "Un escaneo y listo",
    subline: "Respuesta rápida garantizada",
    emoji: "👋",
    palette: "emerald_forest",
    layout: "card",
    background: { type: "gradient" },
    icons: ["hand", "qr", "check"]
  },
  {
    id: "v6",
    category: "vehiculos",
    title: "Conductor Responsable",
    slogan: "Contacto inmediato",
    subline: "Tu tranquilidad, mi compromiso",
    emoji: "✓",
    palette: "trust_blue",
    layout: "badge",
    background: { type: "gradient" },
    icons: ["shield", "check", "star"]
  },
  {
    id: "v7",
    category: "vehiculos",
    title: "¿Urgencia?",
    slogan: "Escanea aquí",
    subline: "Aviso instantáneo al propietario",
    emoji: "🚨",
    palette: "emergency_red",
    layout: "warning",
    background: { type: "solid", pattern: "pulse" },
    icons: ["siren", "alert", "phone"]
  },
  {
    id: "v8",
    category: "vehiculos",
    title: "Mi auto, tu tranquilidad",
    slogan: "Comunicación segura",
    subline: "Sin revelar tu número",
    emoji: "🌟",
    palette: "neon_night",
    layout: "hero",
    background: { type: "gradient", pattern: "stars" },
    icons: ["star", "lock", "heart"]
  },
  {
    id: "v9",
    category: "vehiculos",
    title: "Estacionado pero Conectado",
    slogan: "Avísame si estorbo",
    subline: "Respondo al toque",
    emoji: "🅿️",
    palette: "ocean_blue",
    layout: "card",
    background: { type: "gradient" },
    icons: ["parking", "wifi", "phone"]
  },
  {
    id: "v10",
    category: "vehiculos",
    title: "Siempre Disponible",
    slogan: "Escanea y contacta",
    subline: "24/7 • Privado • Seguro",
    emoji: "📲",
    palette: "arctic_frost",
    layout: "minimal",
    background: { type: "gradient" },
    icons: ["phone", "clock", "shield"]
  },

  // ========================================
  // 🎒 OBJETOS / MALETAS (10 templates)
  // ========================================
  {
    id: "o1",
    category: "objetos",
    title: "¡Gracias por tomarte un segundo!",
    slogan: "Escanea y avisa a mi dueño",
    subline: "Tu ayuda vale mucho",
    emoji: "😊",
    palette: "coral_dream",
    layout: "comic",
    background: { type: "gradient", pattern: "hearts" },
    icons: ["heart", "smile", "star"],
    featured: true
  },
  {
    id: "o2",
    category: "objetos",
    title: "Si fuera tuyo...",
    slogan: "también querrías que vuelva",
    subline: "Escanea aquí",
    emoji: "🥺",
    palette: "golden_hour",
    layout: "hero",
    background: { type: "gradient" },
    icons: ["gift", "return", "heart"]
  },
  {
    id: "o3",
    category: "objetos",
    title: "Ups... creo que me olvidaron",
    slogan: "¿Me ayudas?",
    subline: "Escanea aquí",
    emoji: "😅",
    palette: "coral_dream",
    layout: "comic",
    background: { type: "gradient", pattern: "confetti" },
    icons: ["oops", "help", "phone"]
  },
  {
    id: "o4",
    category: "objetos",
    title: "Perdido pero no olvidado",
    slogan: "Ayúdame a volver a casa",
    subline: "Recompensa asegurada",
    emoji: "🏠",
    palette: "emerald_forest",
    layout: "card",
    background: { type: "gradient" },
    icons: ["home", "gift", "heart"]
  },
  {
    id: "o5",
    category: "objetos",
    title: "Mi dueño me extraña",
    slogan: "Sé un héroe hoy",
    subline: "Un escaneo puede hacer la diferencia",
    emoji: "💔",
    palette: "candy_pink",
    layout: "hero",
    background: { type: "gradient" },
    icons: ["heart-broken", "hero", "star"]
  },
  {
    id: "o6",
    category: "objetos",
    title: "Encontrarme fue fácil",
    slogan: "Devolverme también",
    subline: "Escanea y contacta",
    emoji: "😊",
    palette: "arctic_frost",
    layout: "badge",
    background: { type: "gradient" },
    icons: ["smile", "qr", "check"]
  },
  {
    id: "o7",
    category: "objetos",
    title: "Escanea aquí",
    slogan: "y sé el héroe del día",
    subline: "Tu buena acción cuenta",
    emoji: "🦸",
    palette: "coral_dream",
    layout: "comic",
    background: { type: "gradient", pattern: "stars" },
    icons: ["hero", "cape", "star"]
  },
  {
    id: "o8",
    category: "objetos",
    title: "Soy importante para alguien",
    slogan: "Ayúdame a volver",
    subline: "Contacto fácil y rápido",
    emoji: "❤️",
    palette: "emergency_red",
    layout: "hero",
    background: { type: "gradient" },
    icons: ["heart", "arrow", "home"]
  },
  {
    id: "o9",
    category: "objetos",
    title: "No me dejes aquí",
    slogan: "Escanea y avisa",
    subline: "Mi dueño espera por mí",
    emoji: "🙏",
    palette: "neon_night",
    layout: "card",
    background: { type: "gradient" },
    icons: ["pray", "wait", "phone"]
  },
  {
    id: "o10",
    category: "objetos",
    title: "Recompensa garantizada",
    slogan: "Por devolverme a casa",
    subline: "Escanea para más info",
    emoji: "🎁",
    palette: "golden_hour",
    layout: "badge",
    background: { type: "gradient", pattern: "sparkles" },
    icons: ["gift", "money", "star"]
  },

  // ========================================
  // 🧒 NIÑOS (10 templates)
  // ========================================
  {
    id: "n1",
    category: "ninos",
    title: "Estoy protegido",
    slogan: "Solo escanea y avisa",
    subline: "Mis papás están cerca",
    emoji: "🧒",
    palette: "rainbow_pop",
    layout: "hero",
    background: { type: "gradient", pattern: "stars" },
    icons: ["child", "shield", "heart"],
    featured: true
  },
  {
    id: "n2",
    category: "ninos",
    title: "Si me pierdo, escanea aquí",
    slogan: "Mi familia me espera",
    subline: "Contacto inmediato con mis papás",
    emoji: "👨‍👩‍👧",
    palette: "ocean_blue",
    layout: "card",
    background: { type: "gradient" },
    icons: ["family", "phone", "heart"]
  },
  {
    id: "n3",
    category: "ninos",
    title: "Soy un explorador protegido",
    slogan: "Pero a veces me pierdo",
    subline: "Escanea y avisa a mis papás",
    emoji: "🧭",
    palette: "golden_hour",
    layout: "comic",
    background: { type: "gradient", pattern: "adventure" },
    icons: ["compass", "map", "star"]
  },
  {
    id: "n4",
    category: "ninos",
    title: "Pequeño pero conectado",
    slogan: "Escanea si necesito ayuda",
    subline: "Mi familia recibe el aviso",
    emoji: "🌟",
    palette: "candy_pink",
    layout: "hero",
    background: { type: "gradient" },
    icons: ["star", "wifi", "heart"]
  },
  {
    id: "n5",
    category: "ninos",
    title: "Mis héroes me cuidan",
    slogan: "Pero tú también puedes ayudar",
    subline: "Un escaneo me protege",
    emoji: "🦸‍♂️",
    palette: "trust_blue",
    layout: "comic",
    background: { type: "gradient", pattern: "cape" },
    icons: ["hero", "shield", "star"]
  },
  {
    id: "n6",
    category: "ninos",
    title: "Siempre seguro",
    slogan: "con AvisoCar",
    subline: "Escanea y mis papás lo saben",
    emoji: "🛡️",
    palette: "emerald_forest",
    layout: "badge",
    background: { type: "gradient" },
    icons: ["shield", "check", "family"]
  },
  {
    id: "n7",
    category: "ninos",
    title: "Un angelito protegido",
    slogan: "Ayúdame a encontrar a mamá",
    subline: "Escanea aquí",
    emoji: "👼",
    palette: "arctic_frost",
    layout: "hero",
    background: { type: "gradient", pattern: "clouds" },
    icons: ["angel", "heart", "mom"]
  },
  {
    id: "n8",
    category: "ninos",
    title: "Escanea y avisa a mis papás",
    slogan: "Es fácil y rápido",
    subline: "Gracias por cuidarme",
    emoji: "📱",
    palette: "lime_energy",
    layout: "card",
    background: { type: "gradient" },
    icons: ["phone", "parents", "heart"]
  },
  {
    id: "n9",
    category: "ninos",
    title: "Mi familia me espera",
    slogan: "Si me ves perdido, escanea",
    subline: "Tu ayuda es muy importante",
    emoji: "🏡",
    palette: "coral_dream",
    layout: "hero",
    background: { type: "gradient" },
    icons: ["home", "family", "heart"]
  },
  {
    id: "n10",
    category: "ninos",
    title: "Aventurero pero ubicable",
    slogan: "Mis papás saben dónde estoy",
    subline: "Gracias a ti y a AvisoCar",
    emoji: "🗺️",
    palette: "golden_hour",
    layout: "comic",
    background: { type: "gradient", pattern: "map" },
    icons: ["map", "pin", "family"]
  },

  // ========================================
  // 🐶 MASCOTAS (10 templates)
  // ========================================
  {
    id: "p1",
    category: "mascotas",
    title: "No estoy perdido...",
    slogan: "solo esperando ayuda",
    subline: "Escanea y avisa a mi humano",
    emoji: "🐕",
    palette: "coral_dream",
    layout: "hero",
    background: { type: "gradient", pattern: "paws" },
    icons: ["dog", "heart", "home"],
    featured: true
  },
  {
    id: "p2",
    category: "mascotas",
    title: "Mi humano me busca",
    slogan: "Ayúdame a volver",
    subline: "Un escaneo me salva",
    emoji: "🐶",
    palette: "golden_hour",
    layout: "comic",
    background: { type: "gradient" },
    icons: ["dog", "search", "heart"]
  },
  {
    id: "p3",
    category: "mascotas",
    title: "Si viajo contigo...",
    slogan: "avisa a mi dueño",
    subline: "Escanea este QR",
    emoji: "✈️",
    palette: "neon_night",
    layout: "card",
    background: { type: "gradient", pattern: "clouds" },
    icons: ["plane", "pet", "luggage"]
  },
  {
    id: "p4",
    category: "mascotas",
    title: "Patitas que buscan hogar",
    slogan: "Mi familia me extraña",
    subline: "Ayúdame a volver",
    emoji: "🐾",
    palette: "candy_pink",
    layout: "hero",
    background: { type: "gradient", pattern: "paws" },
    icons: ["paw", "heart", "home"]
  },
  {
    id: "p5",
    category: "mascotas",
    title: "Soy un buen chico",
    slogan: "solo estoy perdido",
    subline: "Escanea y sé mi héroe",
    emoji: "🥺",
    palette: "ocean_blue",
    layout: "comic",
    background: { type: "gradient" },
    icons: ["dog", "hero", "star"]
  },
  {
    id: "p6",
    category: "mascotas",
    title: "Mi collar tiene mi historia",
    slogan: "Escanea y conócela",
    subline: "Ayúdame a volver a casa",
    emoji: "📖",
    palette: "emerald_forest",
    layout: "card",
    background: { type: "gradient" },
    icons: ["collar", "book", "home"]
  },
  {
    id: "p7",
    category: "mascotas",
    title: "¡Guau! Encontraste mi QR",
    slogan: "Avisa a mi dueño",
    subline: "Te lo agradeceré con lamidas",
    emoji: "🐕‍🦺",
    palette: "golden_hour",
    layout: "comic",
    background: { type: "gradient", pattern: "bones" },
    icons: ["dog", "bone", "heart"]
  },
  {
    id: "p8",
    category: "mascotas",
    title: "Un amigo espera por mí",
    slogan: "Ayúdame a encontrarlo",
    subline: "Escanea aquí",
    emoji: "🤝",
    palette: "trust_blue",
    layout: "hero",
    background: { type: "gradient" },
    icons: ["handshake", "pet", "heart"]
  },
  {
    id: "p9",
    category: "mascotas",
    title: "No estoy perdido, solo exploro",
    slogan: "Pero mi humano se preocupa",
    subline: "Avísale que estoy bien",
    emoji: "🐾",
    palette: "lime_energy",
    layout: "comic",
    background: { type: "gradient", pattern: "adventure" },
    icons: ["paw", "explore", "phone"]
  },
  {
    id: "p10",
    category: "mascotas",
    title: "¿Miau? ¿Me encontraste?",
    slogan: "Escanea y avisa a mi humano",
    subline: "Prometo portarme bien",
    emoji: "🐱",
    palette: "neon_night",
    layout: "hero",
    background: { type: "gradient", pattern: "stars" },
    icons: ["cat", "moon", "heart"]
  },

  // ========================================
  // 🧳 VIAJES (10 templates)
  // ========================================
  {
    id: "t1",
    category: "viajes",
    title: "Viajero conectado",
    slogan: "Mi maleta tiene dueño",
    subline: "Escanea si me encuentras",
    emoji: "✈️",
    palette: "ocean_blue",
    layout: "hero",
    background: { type: "gradient", pattern: "clouds" },
    icons: ["plane", "luggage", "world"],
    featured: true
  },
  {
    id: "t2",
    category: "viajes",
    title: "Si me pierdo en el viaje",
    slogan: "Escanea y contacta",
    subline: "Mi dueño espera por mí",
    emoji: "🧳",
    palette: "coral_dream",
    layout: "card",
    background: { type: "gradient" },
    icons: ["luggage", "phone", "map"]
  },
  {
    id: "t3",
    category: "viajes",
    title: "Del aeropuerto a tus manos",
    slogan: "Ayúdame a llegar",
    subline: "Escanea aquí",
    emoji: "🛫",
    palette: "trust_blue",
    layout: "badge",
    background: { type: "gradient" },
    icons: ["plane", "arrow", "check"]
  },
  {
    id: "t4",
    category: "viajes",
    title: "Equipaje inteligente",
    slogan: "Siempre conectado a su dueño",
    subline: "Un escaneo y listo",
    emoji: "💡",
    palette: "golden_hour",
    layout: "hero",
    background: { type: "gradient", pattern: "sparkles" },
    icons: ["bulb", "luggage", "wifi"]
  },
  {
    id: "t5",
    category: "viajes",
    title: "Aventurero pero ubicable",
    slogan: "Mi dueño sabe de mí",
    subline: "Gracias a AvisoCar",
    emoji: "🗺️",
    palette: "emerald_forest",
    layout: "comic",
    background: { type: "gradient", pattern: "map" },
    icons: ["map", "pin", "star"]
  },
  {
    id: "t6",
    category: "viajes",
    title: "Recuerdos que vuelven a casa",
    slogan: "No dejes que se pierdan",
    subline: "Escanea si me encuentras",
    emoji: "🏠",
    palette: "candy_pink",
    layout: "hero",
    background: { type: "gradient" },
    icons: ["home", "heart", "camera"]
  },
  {
    id: "t7",
    category: "viajes",
    title: "Mochilero protegido",
    slogan: "Mi info está en el QR",
    subline: "Escanea y contacta",
    emoji: "🎒",
    palette: "lime_energy",
    layout: "card",
    background: { type: "gradient" },
    icons: ["backpack", "qr", "phone"]
  },
  {
    id: "t8",
    category: "viajes",
    title: "Turista responsable",
    slogan: "Mi equipaje tiene identidad",
    subline: "Escanea para más info",
    emoji: "🌍",
    palette: "ocean_blue",
    layout: "badge",
    background: { type: "gradient", pattern: "world" },
    icons: ["globe", "id", "check"]
  },
  {
    id: "t9",
    category: "viajes",
    title: "Pasaporte a la tranquilidad",
    slogan: "Tu info segura en el QR",
    subline: "Viaja sin preocupaciones",
    emoji: "🛂",
    palette: "neon_night",
    layout: "hero",
    background: { type: "gradient" },
    icons: ["passport", "shield", "plane"]
  },
  {
    id: "t10",
    category: "viajes",
    title: "Mi maleta, mi identidad",
    slogan: "Escanea si te la encontraste",
    subline: "Gracias por tu ayuda",
    emoji: "🧳",
    palette: "arctic_frost",
    layout: "card",
    background: { type: "gradient" },
    icons: ["luggage", "id", "heart"]
  },

  // ========================================
  // 🚨 EMERGENCIAS (10 templates)
  // ========================================
  {
    id: "e1",
    category: "emergencias",
    title: "EN CASO DE EMERGENCIA",
    slogan: "Escanea este código",
    subline: "Información médica vital",
    emoji: "🚨",
    palette: "emergency_red",
    layout: "warning",
    background: { type: "solid", pattern: "warning-stripes" },
    icons: ["siren", "medical", "phone"],
    featured: true
  },
  {
    id: "e2",
    category: "emergencias",
    title: "Información médica vital",
    slogan: "Acceso rápido en emergencias",
    subline: "Escanea para ver mis datos",
    emoji: "🏥",
    palette: "trust_blue",
    layout: "badge",
    background: { type: "gradient" },
    icons: ["hospital", "heart", "plus"]
  },
  {
    id: "e3",
    category: "emergencias",
    title: "Contacto de emergencia aquí",
    slogan: "Información crítica",
    subline: "Escanea y actúa rápido",
    emoji: "📞",
    palette: "emergency_red",
    layout: "hero",
    background: { type: "gradient" },
    icons: ["phone", "alert", "fast"]
  },
  {
    id: "e4",
    category: "emergencias",
    title: "Si me pasa algo",
    slogan: "Escanea y contacta",
    subline: "Mi familia debe saber",
    emoji: "⚠️",
    palette: "warning_zone",
    layout: "warning",
    background: { type: "solid", pattern: "alert" },
    icons: ["warning", "family", "phone"]
  },
  {
    id: "e5",
    category: "emergencias",
    title: "Datos críticos de salud",
    slogan: "Alergias e información",
    subline: "Escanea en caso de emergencia",
    emoji: "💊",
    palette: "emerald_forest",
    layout: "card",
    background: { type: "gradient" },
    icons: ["pill", "allergy", "info"]
  },
  {
    id: "e6",
    category: "emergencias",
    title: "Ayuda inmediata",
    slogan: "Escanea para información",
    subline: "Cada segundo cuenta",
    emoji: "🆘",
    palette: "emergency_red",
    layout: "warning",
    background: { type: "solid", pattern: "pulse" },
    icons: ["sos", "clock", "phone"]
  },
  {
    id: "e7",
    category: "emergencias",
    title: "Mi vida en un código",
    slogan: "Información que salva",
    subline: "Escanea y actúa",
    emoji: "❤️‍🩹",
    palette: "candy_pink",
    layout: "hero",
    background: { type: "gradient" },
    icons: ["heart-heal", "code", "life"]
  },
  {
    id: "e8",
    category: "emergencias",
    title: "Alergias e información",
    slogan: "Datos importantes",
    subline: "Escanea antes de actuar",
    emoji: "⚕️",
    palette: "trust_blue",
    layout: "badge",
    background: { type: "gradient" },
    icons: ["medical", "allergy", "info"]
  },
  {
    id: "e9",
    category: "emergencias",
    title: "Primeros auxilios",
    slogan: "Escanea para instrucciones",
    subline: "Información vital aquí",
    emoji: "🩺",
    palette: "emergency_red",
    layout: "card",
    background: { type: "gradient" },
    icons: ["stethoscope", "aid", "check"]
  },
  {
    id: "e10",
    category: "emergencias",
    title: "ICE - En caso de emergencia",
    slogan: "Contacta a mi familia",
    subline: "Información crítica",
    emoji: "🧊",
    palette: "arctic_frost",
    layout: "hero",
    background: { type: "gradient" },
    icons: ["ice", "family", "phone"]
  },

  // ========================================
  // 🔒 PRIVACIDAD (10 templates)
  // ========================================
  {
    id: "pr1",
    category: "privacidad",
    title: "Puedes ayudar",
    slogan: "sin ver mi número",
    subline: "Contacto seguro con AvisoCar",
    emoji: "🔒",
    palette: "neon_night",
    layout: "hero",
    background: { type: "gradient", pattern: "lock" },
    icons: ["lock", "shield", "phone"],
    featured: true
  },
  {
    id: "pr2",
    category: "privacidad",
    title: "Comunicación privada",
    slogan: "Tu privacidad protegida",
    subline: "Contacta sin revelar tu número",
    emoji: "🕵️",
    palette: "midnight_pro",
    layout: "badge",
    background: { type: "gradient" },
    icons: ["spy", "lock", "shield"]
  },
  {
    id: "pr3",
    category: "privacidad",
    title: "Tu privacidad importa",
    slogan: "La mía también",
    subline: "Contacto privado y seguro",
    emoji: "🛡️",
    palette: "trust_blue",
    layout: "card",
    background: { type: "gradient" },
    icons: ["shield", "heart", "lock"]
  },
  {
    id: "pr4",
    category: "privacidad",
    title: "Avísame sin saber quién soy",
    slogan: "Contacto privado con AvisoCar",
    subline: "Tu dato no se comparte",
    emoji: "❓",
    palette: "neon_night",
    layout: "hero",
    background: { type: "gradient", pattern: "mystery" },
    icons: ["question", "mask", "lock"]
  },
  {
    id: "pr5",
    category: "privacidad",
    title: "Seguridad primero",
    slogan: "Datos cifrados",
    subline: "Comunicación protegida",
    emoji: "🔐",
    palette: "emerald_forest",
    layout: "badge",
    background: { type: "gradient" },
    icons: ["lock", "key", "shield"]
  },
  {
    id: "pr6",
    category: "privacidad",
    title: "Sin spam, solo avisos",
    slogan: "Tu tranquilidad garantizada",
    subline: "Contacto responsable",
    emoji: "🚫",
    palette: "emergency_red",
    layout: "card",
    background: { type: "gradient" },
    icons: ["no-spam", "check", "phone"]
  },
  {
    id: "pr7",
    category: "privacidad",
    title: "Contacto discreto",
    slogan: "Profesional y seguro",
    subline: "Tu número no se revela",
    emoji: "🤫",
    palette: "midnight_pro",
    layout: "hero",
    background: { type: "gradient" },
    icons: ["quiet", "lock", "pro"]
  },
  {
    id: "pr8",
    category: "privacidad",
    title: "Mi info, mi control",
    slogan: "Tú decides qué compartes",
    subline: "Privacidad garantizada",
    emoji: "⚙️",
    palette: "arctic_frost",
    layout: "badge",
    background: { type: "gradient" },
    icons: ["settings", "control", "lock"]
  },
  {
    id: "pr9",
    category: "privacidad",
    title: "Protegido por AvisoCar",
    slogan: "Comunicación segura",
    subline: "Tu privacidad es nuestra prioridad",
    emoji: "✓",
    palette: "emerald_forest",
    layout: "card",
    background: { type: "gradient" },
    icons: ["check", "shield", "star"]
  },
  {
    id: "pr10",
    category: "privacidad",
    title: "Contacto sin revelar número",
    slogan: "Simple, seguro, privado",
    subline: "Escanea y comunícate",
    emoji: "📵",
    palette: "trust_blue",
    layout: "hero",
    background: { type: "gradient" },
    icons: ["no-phone", "lock", "qr"]
  },

  // ========================================
  // ✨ CREATIVOS / DIVERTIDOS (10 templates)
  // ========================================
  {
    id: "c1",
    category: "creativos",
    title: "¡Ups! ¿Te bloqué?",
    slogan: "Mil disculpas",
    subline: "Escanea y avísame",
    emoji: "😅",
    palette: "golden_hour",
    layout: "comic",
    background: { type: "gradient", pattern: "oops" },
    icons: ["oops", "sorry", "phone"],
    featured: true
  },
  {
    id: "c2",
    category: "creativos",
    title: "No te enojes",
    slogan: "Escanea primero",
    subline: "Prometo mover el auto",
    emoji: "😎",
    palette: "coral_dream",
    layout: "comic",
    background: { type: "gradient" },
    icons: ["cool", "car", "move"]
  },
  {
    id: "c3",
    category: "creativos",
    title: "Karma instantáneo",
    slogan: "Si me ayudas, te llega",
    subline: "Escanea aquí",
    emoji: "🔄",
    palette: "rainbow_pop",
    layout: "hero",
    background: { type: "gradient", pattern: "sparkles" },
    icons: ["karma", "star", "heart"]
  },
  {
    id: "c4",
    category: "creativos",
    title: "Mi carro, mi responsabilidad",
    slogan: "Avísame si estorbo",
    subline: "Soy un buen vecino",
    emoji: "💪",
    palette: "emerald_forest",
    layout: "card",
    background: { type: "gradient" },
    icons: ["strong", "car", "neighbor"]
  },
  {
    id: "c5",
    category: "creativos",
    title: "Vecino amigable",
    slogan: "Error de principiante",
    subline: "Pero responsable",
    emoji: "🏘️",
    palette: "ocean_blue",
    layout: "comic",
    background: { type: "gradient" },
    icons: ["house", "smile", "car"]
  },
  {
    id: "c6",
    category: "creativos",
    title: "Parking level: Experto",
    slogan: "Bueno... casi",
    subline: "Escanea si fallé",
    emoji: "🏆",
    palette: "golden_hour",
    layout: "badge",
    background: { type: "gradient", pattern: "trophy" },
    icons: ["trophy", "parking", "oops"]
  },
  {
    id: "c7",
    category: "creativos",
    title: "Estacioné como pude",
    slogan: "No me juzgues",
    subline: "Escanea si molesté",
    emoji: "🤷",
    palette: "candy_pink",
    layout: "comic",
    background: { type: "gradient" },
    icons: ["shrug", "car", "sorry"]
  },
  {
    id: "c8",
    category: "creativos",
    title: "Perdona el bloqueo",
    slogan: "Fue sin querer",
    subline: "Avísame y lo resuelvo",
    emoji: "🙏",
    palette: "neon_night",
    layout: "hero",
    background: { type: "gradient" },
    icons: ["pray", "heart", "fast"]
  },
  {
    id: "c9",
    category: "creativos",
    title: "El mejor vecino del parqueo",
    slogan: "O al menos lo intento",
    subline: "Escanea si no lo logré",
    emoji: "⭐",
    palette: "lime_energy",
    layout: "card",
    background: { type: "gradient", pattern: "stars" },
    icons: ["star", "neighbor", "car"]
  },
  {
    id: "c10",
    category: "creativos",
    title: "Plot twist: Soy responsable",
    slogan: "Escanea y verás",
    subline: "Te respondo al toque",
    emoji: "🎬",
    palette: "midnight_pro",
    layout: "hero",
    background: { type: "gradient", pattern: "film" },
    icons: ["film", "twist", "phone"]
  },

  // ========================================
  // 🏷️ MINIMALISTA (10 templates)
  // ========================================
  {
    id: "m1",
    category: "minimalista",
    title: "Escanea",
    slogan: "Contacta",
    subline: "",
    emoji: "",
    palette: "midnight_pro",
    layout: "minimal",
    background: { type: "solid", color: "#FFFFFF" },
    icons: [],
    featured: true
  },
  {
    id: "m2",
    category: "minimalista",
    title: "Contacto",
    slogan: "Escanea aquí",
    subline: "",
    emoji: "",
    palette: "midnight_pro",
    layout: "minimal",
    background: { type: "solid", color: "#F8FAFC" },
    icons: []
  },
  {
    id: "m3",
    category: "minimalista",
    title: "AvisoCar",
    slogan: "",
    subline: "",
    emoji: "",
    palette: "emerald_forest",
    layout: "minimal",
    background: { type: "solid", color: "#FFFFFF" },
    icons: []
  },
  {
    id: "m4",
    category: "minimalista",
    title: "Avísame",
    slogan: "",
    subline: "",
    emoji: "",
    palette: "trust_blue",
    layout: "minimal",
    background: { type: "solid", color: "#FFFFFF" },
    icons: []
  },
  {
    id: "m5",
    category: "minimalista",
    title: "QR",
    slogan: "Escanea",
    subline: "",
    emoji: "",
    palette: "midnight_pro",
    layout: "minimal",
    background: { type: "solid", color: "#F1F5F9" },
    icons: []
  },
  {
    id: "m6",
    category: "minimalista",
    title: "→",
    slogan: "Scan",
    subline: "",
    emoji: "",
    palette: "midnight_pro",
    layout: "minimal",
    background: { type: "solid", color: "#FFFFFF" },
    icons: ["arrow"]
  },
  {
    id: "m7",
    category: "minimalista",
    title: "Info",
    slogan: "Escanea",
    subline: "",
    emoji: "",
    palette: "neon_night",
    layout: "minimal",
    background: { type: "solid", color: "#FFFFFF" },
    icons: []
  },
  {
    id: "m8",
    category: "minimalista",
    title: "Scan",
    slogan: "",
    subline: "",
    emoji: "",
    palette: "midnight_pro",
    layout: "minimal",
    background: { type: "solid", color: "#FFFFFF" },
    icons: []
  },
  {
    id: "m9",
    category: "minimalista",
    title: ".",
    slogan: "",
    subline: "",
    emoji: "",
    palette: "midnight_pro",
    layout: "minimal",
    background: { type: "solid", color: "#0F172A" },
    icons: []
  },
  {
    id: "m10",
    category: "minimalista",
    title: "AC",
    slogan: "",
    subline: "",
    emoji: "",
    palette: "emerald_forest",
    layout: "minimal",
    background: { type: "solid", color: "#FFFFFF" },
    icons: []
  },

  // ========================================
  // ⭐ PREMIUM LATAM (10 templates - Peruanismos)
  // ========================================
  {
    id: "l1",
    category: "premium_latam",
    title: "¡Causa, avísame!",
    slogan: "Si bloqueo tu carro",
    subline: "Entre peruanos nos ayudamos",
    emoji: "🇵🇪",
    palette: "peru_pride",
    layout: "hero",
    background: { type: "split", colors: ["#DC2626", "#FFFFFF"] },
    icons: ["flag-peru", "handshake", "car"],
    featured: true
  },
  {
    id: "l2",
    category: "premium_latam",
    title: "Entre peruanos",
    slogan: "nos ayudamos",
    subline: "Escanea y contacta seguro",
    emoji: "🤝",
    palette: "emerald_forest",
    layout: "card",
    background: { type: "gradient" },
    icons: ["handshake", "flag-peru", "heart"]
  },
  {
    id: "l3",
    category: "premium_latam",
    title: "Pata, escanea aquí nomás",
    slogan: "Al toque te respondo",
    subline: "Palabra de peruano",
    emoji: "👊",
    palette: "golden_hour",
    layout: "comic",
    background: { type: "gradient" },
    icons: ["fist", "fast", "phone"]
  },
  {
    id: "l4",
    category: "premium_latam",
    title: "Pa' que no te amargues",
    slogan: "Escanea y avísame",
    subline: "Lo resuelvo al toque",
    emoji: "😄",
    palette: "coral_dream",
    layout: "hero",
    background: { type: "gradient" },
    icons: ["smile", "fast", "check"]
  },
  {
    id: "l5",
    category: "premium_latam",
    title: "Chamba bien hecha",
    slogan: "Soy responsable con mi carro",
    subline: "Escanea si necesitas algo",
    emoji: "💼",
    palette: "trust_blue",
    layout: "badge",
    background: { type: "gradient" },
    icons: ["work", "car", "pro"]
  },
  {
    id: "l6",
    category: "premium_latam",
    title: "Al toque te aviso",
    slogan: "Solo escanea",
    subline: "Rapidito como ceviche",
    emoji: "⚡",
    palette: "golden_hour",
    layout: "card",
    background: { type: "gradient", pattern: "lightning" },
    icons: ["lightning", "fast", "phone"]
  },
  {
    id: "l7",
    category: "premium_latam",
    title: "Buena onda siempre",
    slogan: "Escanea y conversamos",
    subline: "Sin problemas, pata",
    emoji: "🌊",
    palette: "ocean_blue",
    layout: "hero",
    background: { type: "gradient", pattern: "waves" },
    icons: ["wave", "smile", "chat"]
  },
  {
    id: "l8",
    category: "premium_latam",
    title: "De ley te contacto",
    slogan: "Palabra de honor",
    subline: "Escanea nomás",
    emoji: "🤙",
    palette: "candy_pink",
    layout: "comic",
    background: { type: "gradient" },
    icons: ["call", "promise", "heart"]
  },
  {
    id: "l9",
    category: "premium_latam",
    title: "Bacán y seguro",
    slogan: "Así es el contacto",
    subline: "Via AvisoCar",
    emoji: "😎",
    palette: "neon_night",
    layout: "hero",
    background: { type: "gradient", pattern: "cool" },
    icons: ["cool", "lock", "star"]
  },
  {
    id: "l10",
    category: "premium_latam",
    title: "Hecho en Perú",
    slogan: "con mucho cariño",
    subline: "AvisoCar 💚",
    emoji: "🇵🇪",
    palette: "peru_pride",
    layout: "badge",
    background: { type: "split", colors: ["#DC2626", "#10B981"] },
    icons: ["flag-peru", "heart", "made"]
  },
];

// Helper functions
export const getTemplatesByCategory = (categoryId) => {
  return STICKER_TEMPLATES.filter(t => t.category === categoryId);
};

export const getTemplateById = (templateId) => {
  return STICKER_TEMPLATES.find(t => t.id === templateId) || null;
};

export const getCategoryById = (categoryId) => {
  return STICKER_CATEGORIES.find(c => c.id === categoryId) || null;
};

export const getFeaturedTemplates = () => {
  return STICKER_TEMPLATES.filter(t => t.featured);
};

export const getPalette = (paletteName) => {
  return PALETTES[paletteName] || PALETTES.emerald_forest;
};
